import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { StyleSheet } from "react-native";
import { applePay, googlePay, mastercard, visa } from "../../assets/images";

import {
  Block,
  type IconNames,
  Image,
  SvgIcon,
  Touchable,
  Typography,
  palette,
  spacing,
} from "@stepapp/ui";

const iconMapping = { add: visa, apple: applePay, google: googlePay, visa, mastercard };

type Props = {
  onPress?: () => void;
  type: "apple" | "google" | "visa" | "mastercard" | "add";
  label?: string;
  icon?: IconNames;
  iconColor?: string;
};

const PaymentOption = ({ icon, iconColor, label, onPress, type }: Props) => {
  const { t } = useTranslation();
  const hasWhiteBg = ["chevron", "edit", "chevron-right"]?.includes(icon || "");
  const isEditIcon = icon === "edit";
  const iconSize = isEditIcon ? 18 : icon === "done" ? 28 : 22;
  const cardText = useMemo(
    () => (["apple", "google"].includes(type) ? label : `**** **** **** ${label}`),
    [label, type],
  );

  return (
    <Touchable activeOpacity={onPress ? 1 : 0.6} onPress={onPress} testID="selectPayment">
      <Block
        justifySpace
        row
        borderRadius={1}
        marginBottom={1}
        paddingHorizontal={2}
        paddingVertical={2}
        style={styles.container}
      >
        <Block alignCenter row flex={1}>
          {type && type !== "add" ? (
            <Image
              contentFit="contain"
              height={spacing.unit * 3}
              source={
                type in iconMapping
                  ? iconMapping[type as keyof typeof iconMapping]
                  : iconMapping.visa
              }
              style={styles.icon}
              width={spacing.unit * 5}
            />
          ) : (
            <SvgIcon color={palette.primary.accentDark} name="card" size={22} />
          )}

          <Block paddingHorizontal={1}>
            <Typography type="accentDark" variant="input">
              {type === "add" ? t("screens.serviceSummary.addCreditDebitCard") : cardText}
            </Typography>
          </Block>
        </Block>

        <Block height={4} width={4}>
          {icon && (
            <Block
              alignCenter
              justifyCenter
              height={isEditIcon ? 4.5 : 4}
              style={hasWhiteBg && styles.changeCardIcon}
              width={isEditIcon ? 4.5 : 4}
            >
              <SvgIcon color={iconColor || palette.primary.accent} name={icon} size={iconSize} />
            </Block>
          )}
        </Block>
      </Block>
    </Touchable>
  );
};

export default React.memo(PaymentOption);

const styles = StyleSheet.create({
  changeCardIcon: {
    backgroundColor: palette.common.white,
    borderRadius: spacing.unit * 2.5,
  },
  container: {
    backgroundColor: palette.secondary.main,
    width: "100%",
  },
  icon: {
    height: spacing.unit * 3,
    width: spacing.unit * 5,
  },
});
